import { onINP, type INPMetricWithAttribution } from 'web-vitals/attribution'

import { posthogManager } from './manager'

import { actionTracking } from './'

const LONG_TASK_THRESHOLD_MS = 200
const LONG_TASK_THROTTLE_MS = 500
const SLOW_RESOURCE_THRESHOLD_MS = 1000

let initialized = false

export const initPerformanceObservers = () => {
  if (initialized) return
  if (typeof window === 'undefined') return
  if (typeof PerformanceObserver === 'undefined') return

  initialized = true
  observeLongTasks()
  observeSlowResources()
  observeInpAttribution()
}

// Exposed for tests only.
export const _resetForTests = () => {
  initialized = false
}

const observeLongTasks = () => {
  let lastEmittedAt = 0
  try {
    const observer = new PerformanceObserver(list => {
      // Gate the loop on client readiness so the throttle timer doesn't
      // advance while events would be dropped anyway.
      if (!posthogManager.getClientInstance()) return

      for (const entry of list.getEntries()) {
        if (entry.duration < LONG_TASK_THRESHOLD_MS) continue

        const tsNow = Date.now()
        if (tsNow - lastEmittedAt < LONG_TASK_THROTTLE_MS) continue
        lastEmittedAt = tsNow

        const attribution = (
          entry as PerformanceEntry & {
            attribution?: {
              name?: string
              containerType?: string
              containerSrc?: string
            }[]
          }
        ).attribution?.[0]

        actionTracking({
          action: 'client_long_task',
          properties: {
            duration_ms: Math.round(entry.duration),
            start_time_ms: Math.round(entry.startTime),
            name: entry.name,
            attribution_name: attribution?.name,
            attribution_container_type: attribution?.containerType,
            attribution_container_src: attribution?.containerSrc
          }
        })
      }
    })
    observer.observe({ type: 'longtask', buffered: true })
  } catch {
    // longtask entry type is not supported in some browsers (e.g. Safari <16).
  }
}

const observeSlowResources = () => {
  try {
    const observer = new PerformanceObserver(list => {
      if (!posthogManager.getClientInstance()) return

      for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
        if (entry.duration < SLOW_RESOURCE_THRESHOLD_MS) continue

        // Cross-origin entries without `Timing-Allow-Origin` report 0 for
        // requestStart/responseStart, so the breakdown would be meaningless.
        const hasTiming = entry.requestStart > 0 && entry.responseStart > 0

        actionTracking({
          action: 'client_slow_resource',
          properties: {
            // Strip query string: pre-signed storage URLs (S3/GCS/Azure SAS)
            // carry auth signatures in query params, and per-signature URLs
            // also fragment dashboard aggregation.
            url: entry.name.split('?')[0],
            initiator_type: entry.initiatorType,
            duration_ms: Math.round(entry.duration),
            transfer_size: entry.transferSize,
            encoded_body_size: entry.encodedBodySize,
            decoded_body_size: entry.decodedBodySize,
            next_hop_protocol: entry.nextHopProtocol,
            // Queue + DNS + connect + TLS. High wait_ms with low download_ms
            // means the browser had the resource queued (boot-time congestion),
            // not that the network was slow.
            wait_ms: hasTiming
              ? Math.round(entry.requestStart - entry.startTime)
              : null,
            // Server processing + network round-trip. Low ttfb_ms confirms a
            // CDN HIT regardless of how large `duration_ms` looks.
            ttfb_ms: hasTiming
              ? Math.round(entry.responseStart - entry.requestStart)
              : null,
            // Pure content download. The only split that should scale with
            // `transfer_size` on a healthy network.
            download_ms: hasTiming
              ? Math.round(entry.responseEnd - entry.responseStart)
              : null
          }
        })
      }
    })
    observer.observe({ type: 'resource', buffered: true })
  } catch {
    // resource timing not supported.
  }
}

// Per-interaction INP attribution. The aggregate `$web_vitals` INP tells us a
// route is slow; this tells us *which* interaction (and which element / script)
// caused it, plus the input-delay vs processing vs presentation split — the
// breakdown the INP fix work (ENG-606 / 609 / 613) needs to target the right
// handler instead of guessing.
//
// `reportAllChanges` emits each new worst interaction as it happens rather than
// only on page-hide. INP is monotonic per page, so this is bounded to a handful
// of events, and it avoids losing data for the slowest users whose unload
// beacon never flushes under high RTT.
const observeInpAttribution = () => {
  try {
    onINP(
      (metric: INPMetricWithAttribution) => {
        if (!posthogManager.getClientInstance()) return

        const { attribution } = metric

        // The longest script in the interaction's blocking animation frame —
        // names the offending code directly when the LoAF API is available.
        const topScript = attribution.longAnimationFrameEntries
          ?.flatMap(entry => entry.scripts ?? [])
          .sort((a, b) => b.duration - a.duration)[0]

        actionTracking({
          action: 'client_inp_attribution',
          properties: {
            inp_ms: Math.round(metric.value),
            rating: metric.rating,
            metric_id: metric.id,
            interaction_type: attribution.interactionType,
            // CSS-selector path to the interacted element (e.g. a button).
            target: attribution.interactionTarget,
            // The three phases an interaction's latency splits into.
            input_delay_ms: Math.round(attribution.inputDelay),
            processing_time_ms: Math.round(attribution.processingDuration),
            presentation_delay_ms: Math.round(attribution.presentationDelay),
            next_paint_time_ms: Math.round(attribution.nextPaintTime),
            // Captured at report time: in the Pages Router SPA the pathname can
            // differ from where the interaction happened, so record it explicitly.
            route:
              typeof window !== 'undefined'
                ? window.location?.pathname
                : undefined,
            loaf_count: attribution.longAnimationFrameEntries?.length ?? 0,
            loaf_top_script: topScript?.sourceURL,
            loaf_top_script_invoker: topScript?.invoker,
            loaf_top_script_duration_ms: topScript
              ? Math.round(topScript.duration)
              : undefined
          }
        })
      },
      { reportAllChanges: true }
    )
  } catch {
    // web-vitals onINP is a no-op where the Event Timing API is unsupported.
  }
}
